import React from 'react';
import { ApiHandler, useDonorProfile, Wallet } from '../../../api';

export const useWallet = () => {
    const {
        donorProfile: donor,
        isLoading: donorIsLoading,
        getDonorProfile,
        updateLocalDonorProfile,
    } = useDonorProfile(true);
    const [isUpdating, setIsUpdating] = React.useState(false);
    const [submitError, setSubmitError] = React.useState<string | null>(null);

    const onDeleteWallet = React.useCallback(
        async (wallet: Wallet): Promise<void> => {
            setSubmitError(null);
            setIsUpdating(true);
            const result = await ApiHandler.instance.donor.deleteWallet(
                wallet.id,
            );
            if (result.success) {
                const currentData = getDonorProfile();
                if (currentData) {
                    updateLocalDonorProfile({
                        ...currentData,
                        wallets: currentData.wallets.filter(
                            (w) => w.id !== wallet.id,
                        ),
                    });
                }
            } else {
                setSubmitError(
                    'Unable to delete card. Please try again later.',
                );
            }
            setIsUpdating(false);
        },
        [getDonorProfile, updateLocalDonorProfile],
    );

    const onSetPreferredWallet = React.useCallback(
        async (wallet: Wallet): Promise<void> => {
            setSubmitError(null);
            setIsUpdating(true);
            const result = await ApiHandler.instance.donor.setPreferredWallet(
                wallet.id,
            );
            if (result.success) {
                const currentData = getDonorProfile();
                if (currentData) {
                    updateLocalDonorProfile({
                        ...currentData,
                        wallets: currentData.wallets.map((w) => ({
                            ...w,
                            isPreferred: w.id === wallet.id,
                        })),
                    });
                }
            } else {
                setSubmitError(
                    'Unable to update card status. Please try again later.',
                );
            }
            setIsUpdating(false);
        },
        [getDonorProfile, updateLocalDonorProfile],
    );

    const onDeletePreferredWalletAndSetNew = React.useCallback(
        async (
            deleteAccountId: number,
            newDefaultAccountId: number,
        ): Promise<void> => {
            setSubmitError(null);
            setIsUpdating(true);
            const deleteResult =
                await ApiHandler.instance.donor.deleteWallet(deleteAccountId);
            if (!deleteResult.success) {
                setSubmitError(
                    'Unable to delete card. Please try again later.',
                );
                setIsUpdating(false);
                return;
            }
            const preferResult =
                await ApiHandler.instance.donor.setPreferredWallet(
                    newDefaultAccountId,
                );
            const currentData = getDonorProfile();
            if (preferResult.success) {
                if (currentData) {
                    updateLocalDonorProfile({
                        ...currentData,
                        wallets: currentData.wallets
                            .filter((w) => w.id !== deleteAccountId)
                            .map((w) => ({
                                ...w,
                                isPreferred: w.id === newDefaultAccountId,
                            })),
                    });
                }
            } else {
                if (currentData) {
                    updateLocalDonorProfile({
                        ...currentData,
                        wallets: currentData.wallets.filter(
                            (w) => w.id !== deleteAccountId,
                        ),
                    });
                }
                setSubmitError(
                    'Unable to update card status. Please try again later.',
                );
            }
            setIsUpdating(false);
        },
        [getDonorProfile, updateLocalDonorProfile],
    );

    const dismissAlert = React.useCallback(() => setSubmitError(null), []);

    const metaTags = React.useMemo(
        () => ({
            title: 'Wallet | Givelify',
            description: 'Wallet screen',
        }),
        [],
    );

    return {
        metaTags,
        wallets: donor?.wallets,
        isLoading: donorIsLoading,
        isUpdating,
        submitError,
        dismissAlert,
        onDeleteWallet,
        onSetPreferredWallet,
        onDeletePreferredWalletAndSetNew,
    };
};
